const DEV_RECAPTCHA_HOST = 'dev.ci.acnegocios.mx';
const PROD_RECAPTCHA_HOST = 'ci.acnegocios.mx';
const DEFAULT_RECAPTCHA_ACTION = process.env.REACT_APP_RECAPTCHA_ACTION_LOGIN || 'login_ci';
const DEFAULT_VERIFY_ENDPOINT = '/api/recaptcha/verify';
const RECAPTCHA_ENABLED_FLAG = String(process.env.REACT_APP_RECAPTCHA_ENABLED || '').trim().toLowerCase();

let enterpriseScriptPromise = null;

function createRecaptchaError(code, message, extra = {}) {
  const error = new Error(message);
  error.code = code;
  return Object.assign(error, extra);
}

function getRuntimeHostname() {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }
  return String(window.location.hostname || '').trim().toLowerCase();
}

export function resolveRecaptchaSiteKey(hostname = getRuntimeHostname()) {
  const normalizedHostname = String(hostname || '').trim().toLowerCase();
  if (normalizedHostname === DEV_RECAPTCHA_HOST) {
    return process.env.REACT_APP_RECAPTCHA_CI_DEV_SITE_KEY || '';
  }
  if (normalizedHostname === PROD_RECAPTCHA_HOST) {
    return process.env.REACT_APP_RECAPTCHA_CI_PROD_SITE_KEY || '';
  }
  return '';
}

export function getRecaptchaAction() {
  return DEFAULT_RECAPTCHA_ACTION;
}

export function isRecaptchaEnabled(hostname = getRuntimeHostname()) {
  const siteKey = resolveRecaptchaSiteKey(hostname);
  if (!siteKey) {
    return false;
  }

  if (RECAPTCHA_ENABLED_FLAG === 'true') {
    return true;
  }

  return String(hostname || '').trim().toLowerCase() === DEV_RECAPTCHA_HOST;
}

function ensureRecaptchaScript(siteKey) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(
      createRecaptchaError('RECAPTCHA_BROWSER_ONLY', 'reCAPTCHA solo puede ejecutarse en el navegador')
    );
  }

  if (window.grecaptcha?.enterprise?.ready) {
    return Promise.resolve(window.grecaptcha.enterprise);
  }

  if (enterpriseScriptPromise) {
    return enterpriseScriptPromise;
  }

  enterpriseScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[data-recaptcha-enterprise="true"]');
    if (existingScript && window.grecaptcha?.enterprise?.ready) {
      resolve(window.grecaptcha.enterprise);
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaEnterprise = 'true';
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.onload = () => {
      if (window.grecaptcha?.enterprise?.ready) {
        resolve(window.grecaptcha.enterprise);
        return;
      }
      reject(
        createRecaptchaError(
          'RECAPTCHA_UNAVAILABLE',
          'No fue posible inicializar reCAPTCHA Enterprise en este navegador'
        )
      );
    };
    script.onerror = () => {
      reject(
        createRecaptchaError(
          'RECAPTCHA_UNAVAILABLE',
          'No fue posible cargar reCAPTCHA Enterprise. Reintenta en unos segundos.'
        )
      );
    };

    document.head.appendChild(script);
  });

  return enterpriseScriptPromise;
}

export async function requestLoginRecaptchaToken({ hostname = getRuntimeHostname(), action = DEFAULT_RECAPTCHA_ACTION } = {}) {
  if (!isRecaptchaEnabled(hostname)) {
    return null;
  }

  const siteKey = resolveRecaptchaSiteKey(hostname);
  if (!siteKey) {
    throw createRecaptchaError(
      'RECAPTCHA_CONFIGURATION_MISSING',
      'Falta configurar la llave pública de reCAPTCHA para este entorno'
    );
  }

  const enterprise = await ensureRecaptchaScript(siteKey);

  return new Promise((resolve, reject) => {
    if (typeof enterprise.ready !== 'function') {
      reject(
        createRecaptchaError(
          'RECAPTCHA_UNAVAILABLE',
          'No fue posible inicializar reCAPTCHA Enterprise en este navegador'
        )
      );
      return;
    }

    enterprise.ready(async () => {
      try {
        if (typeof enterprise.execute !== 'function') {
          reject(
            createRecaptchaError(
              'RECAPTCHA_UNAVAILABLE',
              'No fue posible inicializar reCAPTCHA Enterprise en este navegador'
            )
          );
          return;
        }
        const token = await enterprise.execute(siteKey, { action });
        if (!token) {
          reject(
            createRecaptchaError(
              'RECAPTCHA_TOKEN_MISSING',
              'No se pudo generar el token de reCAPTCHA. Reintenta.'
            )
          );
          return;
        }
        resolve(token);
      } catch (error) {
        reject(
          createRecaptchaError(
            'RECAPTCHA_TOKEN_FAILED',
            error?.message || 'No se pudo ejecutar reCAPTCHA. Reintenta.',
            { cause: error }
          )
        );
      }
    });
  });
}

function normalizeVerifyError(payload, status) {
  const code = payload?.code || payload?.errorCode || 'RECAPTCHA_VERIFICATION_FAILED';
  const message = payload?.message || payload?.errorMessage || 'No fue posible verificar reCAPTCHA';
  return createRecaptchaError(code, message, {
    status,
    details: payload
  });
}

export async function verifyLoginRecaptchaToken({
  token,
  action = DEFAULT_RECAPTCHA_ACTION,
  endpoint = DEFAULT_VERIFY_ENDPOINT
} = {}) {
  if (!token) {
    throw createRecaptchaError(
      'RECAPTCHA_TOKEN_MISSING',
      'No se pudo generar el token de reCAPTCHA. Reintenta.'
    );
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ token, action })
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw normalizeVerifyError(payload, response.status);
    }

    if (payload?.ok === false) {
      throw normalizeVerifyError(payload, response.status);
    }

    return payload;
  } catch (error) {
    if (error?.code) {
      throw error;
    }

    throw createRecaptchaError(
      'RECAPTCHA_TEMPORARY_FAILURE',
      'La verificación reCAPTCHA no está disponible temporalmente. Reintenta en unos segundos.',
      { cause: error }
    );
  }
}

export function isTemporaryRecaptchaError(error) {
  return Boolean(
    error &&
      (error.code === 'RECAPTCHA_TEMPORARY_FAILURE' ||
        error.code === 'RECAPTCHA_UNAVAILABLE' ||
        error.code === 'RECAPTCHA_TOKEN_FAILED')
  );
}
